import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Building2, User } from 'lucide-react'

/**
 * Componente riutilizzabile per i campi del form cliente.
 * Usato sia nella pagina ClienteForm che nel dialog ClienteQuickCreateDialog.
 * 
 * @param {Object} props
 * @param {Object} props.formData - Dati del form
 * @param {Object} props.errors - Errori di validazione
 * @param {Function} props.onChange - Handler per cambio input
 * @param {Function} props.onSwitchChange - Handler per cambio switch
 * @param {boolean} props.showCards - Se true, wrappa le sezioni in Card (default: true)
 * @param {boolean} props.compact - Se true, layout più compatto per dialog (default: false)
 */
export default function ClienteFormFields({
  formData,
  errors,
  onChange,
  onSwitchChange,
  venditori = [],
  venditoreField = null,
  showCards = true,
  compact = false,
  billingSection = null,
  referenteRequired = true,
}) {
  const Wrapper = showCards ? Card : 'div'
  const WrapperHeader = showCards ? CardHeader : 'div'
  const WrapperTitle = showCards ? CardTitle : 'h3'
  const WrapperContent = showCards ? CardContent : 'div'

  const titleClass = showCards ? '' : 'text-lg font-semibold mb-3'
  const contentClass = showCards ? 'space-y-4' : 'space-y-4 mb-6'
  const showReferenteRequired = referenteRequired || Object.values(formData.referente_azienda || {}).some(value => value?.trim())

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {/* Tipo Cliente */}
      <Wrapper>
        {showCards && (
          <WrapperHeader>
            <WrapperTitle>Tipo Cliente</WrapperTitle>
          </WrapperHeader>
        )}
        <WrapperContent className={!showCards ? 'mb-4' : ''}>
          {!showCards && <h3 className={titleClass}>Tipo Cliente</h3>}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="tipo"
                value="privato"
                checked={formData.tipo === 'privato'}
                onChange={onChange}
                className="w-4 h-4"
              />
              <User className="h-4 w-4" />
              <span>Privato</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="tipo"
                value="azienda"
                checked={formData.tipo === 'azienda'}
                onChange={onChange}
                className="w-4 h-4"
              />
              <Building2 className="h-4 w-4" />
              <span>Azienda</span>
            </label>
          </div>

          {/* Venditore che ha acquisito il contatto (Punto 3 feedback cliente) */}
          <div className="space-y-2 mt-4">
            <Label htmlFor="venditore">
              Venditore che ha preso il contatto <span className="text-red-500">*</span>
            </Label>
            {venditoreField || (
              <>
                <select
                  id="venditore"
                  name="venditore"
                  value={formData.venditore || ''}
                  onChange={onChange}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">Seleziona venditore...</option>
                  {venditori.map((v) => (
                    <option key={v.id} value={v.id}>
                      {[v.nome, v.cognome].filter(Boolean).join(' ') || `Venditore #${v.id}`}
                    </option>
                  ))}
                </select>
                {errors.venditore && (
                  <p className="text-sm text-red-500">{errors.venditore}</p>
                )}
              </>
            )}
          </div>
        </WrapperContent>
      </Wrapper>

      {/* Il referente deve essere subito visibile dopo il venditore. */}
      {formData.tipo === 'azienda' && (
        <Wrapper>
          {showCards && (
            <WrapperHeader>
              <WrapperTitle>Referente aziendale</WrapperTitle>
            </WrapperHeader>
          )}
          <WrapperContent className={contentClass}>
            {!showCards && <h3 className={titleClass}>Referente aziendale</h3>}
            {!referenteRequired && (
              <p className="text-sm text-muted-foreground">
                Cliente esistente: puoi aggiungere il referente in seguito.
              </p>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              {[
                ['nome', 'Nome', 'text'],
                ['cognome', 'Cognome', 'text'],
                ['telefono', 'Telefono', 'tel'],
                ['email', 'Email', 'email'],
              ].map(([field, label, type]) => (
                <div className="space-y-2" key={field}>
                  <Label htmlFor={`referente_${field}`}>
                    {label}{' '}
                    {field === 'email'
                      ? '(facoltativa)'
                      : showReferenteRequired && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id={`referente_${field}`}
                    name={`referente_${field}`}
                    type={type}
                    value={formData.referente_azienda?.[field] || ''}
                    onChange={onChange}
                  />
                  {errors.referente_azienda?.[field] && (
                    <p className="text-sm text-red-500">{errors.referente_azienda[field]}</p>
                  )}
                </div>
              ))}
            </div>
            {typeof errors.referente_azienda === 'string' && (
              <p className="text-sm text-red-500">{errors.referente_azienda}</p>
            )}
          </WrapperContent>
        </Wrapper>
      )}

      {/* Dati Anagrafici */}
      <Wrapper>
        {showCards && (
          <WrapperHeader>
            <WrapperTitle>Dati Anagrafici</WrapperTitle>
          </WrapperHeader>
        )}
        <WrapperContent className={contentClass}>
          {!showCards && <h3 className={titleClass}>Dati Anagrafici</h3>}
          {formData.tipo === 'privato' ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="nome">
                    Nome <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={onChange}
                    placeholder="Mario"
                  />
                  {errors.nome && (
                    <p className="text-sm text-red-500">{errors.nome}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cognome">
                    Cognome <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="cognome"
                    name="cognome"
                    value={formData.cognome}
                    onChange={onChange}
                    placeholder="Rossi"
                  />
                  {errors.cognome && (
                    <p className="text-sm text-red-500">{errors.cognome}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                <Input
                  id="codice_fiscale"
                  name="codice_fiscale"
                  value={formData.codice_fiscale}
                  onChange={onChange}
                  placeholder="RSSMRA80A01H501U"
                  maxLength={16}
                  className="uppercase"
                />
                {errors.codice_fiscale && (
                  <p className="text-sm text-red-500">{errors.codice_fiscale}</p>
                )}
                <p className="text-sm text-muted-foreground">16 caratteri</p>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="ragione_sociale">
                  Ragione Sociale <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="ragione_sociale"
                  name="ragione_sociale"
                  value={formData.ragione_sociale}
                  onChange={onChange}
                  placeholder="Azienda S.r.l."
                />
                {errors.ragione_sociale && (
                  <p className="text-sm text-red-500">{errors.ragione_sociale}</p>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="partita_iva">Partita IVA</Label>
                  <Input
                    id="partita_iva"
                    name="partita_iva"
                    value={formData.partita_iva}
                    onChange={onChange}
                    placeholder="12345678901"
                    maxLength={11}
                  />
                  {errors.partita_iva && (
                    <p className="text-sm text-red-500">{errors.partita_iva}</p>
                  )}
                  <p className="text-sm text-muted-foreground">11 caratteri</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="codice_sdi">Codice SDI (facoltativo)</Label>
                  <Input
                    id="codice_sdi"
                    name="codice_sdi"
                    value={formData.codice_sdi}
                    onChange={onChange}
                    placeholder="ABCDEFG"
                    maxLength={7}
                    className="uppercase"
                  />
                  {errors.codice_sdi && (
                    <p className="text-sm text-red-500">{errors.codice_sdi}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Codice destinatario per fatturazione elettronica (7 caratteri)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pec">PEC (facoltativa)</Label>
                  <Input
                    id="pec"
                    name="pec"
                    type="email"
                    value={formData.pec}
                    onChange={onChange}
                    placeholder="pec@pec.it"
                  />
                  {errors.pec && <p className="text-sm text-red-500">{errors.pec}</p>}
                </div>
              </div>
            </>
          )}
        </WrapperContent>
      </Wrapper>

      {/* Contatti */}
      <Wrapper>
        {showCards && (
          <WrapperHeader>
            <WrapperTitle>Contatti</WrapperTitle>
          </WrapperHeader>
        )}
        <WrapperContent className={contentClass}>
          {!showCards && <h3 className={titleClass}>Contatti</h3>}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="email">
                Email (facoltativa)
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={onChange}
                placeholder="email@esempio.it"
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefono">
                Telefono {formData.tipo === 'privato'
                  ? <span className="text-red-500">*</span>
                  : '(facoltativo)'}
              </Label>
              <Input
                id="telefono"
                name="telefono"
                type="tel"
                value={formData.telefono}
                onChange={onChange}
                placeholder="+39 081 1234567"
              />
              {errors.telefono && (
                <p className="text-sm text-red-500">{errors.telefono}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="cellulare">
                Cellulare{formData.tipo === 'azienda' ? ' (facoltativo)' : ''}
              </Label>
              <Input
                id="cellulare"
                name="cellulare"
                type="tel"
                value={formData.cellulare}
                onChange={onChange}
                placeholder="+39 333 1234567"
              />
            </div>

            {formData.tipo === 'privato' && (
              <div className="space-y-2">
                <Label htmlFor="pec">PEC (facoltativa)</Label>
                <Input id="pec" name="pec" type="email" value={formData.pec} onChange={onChange} placeholder="pec@pec.it" />
                {errors.pec && <p className="text-sm text-red-500">{errors.pec}</p>}
              </div>
            )}
          </div>
        </WrapperContent>
      </Wrapper>

      {billingSection}

      {/* Note */}
      <Wrapper>
        {showCards && (
          <WrapperHeader>
            <WrapperTitle>Note</WrapperTitle>
          </WrapperHeader>
        )}
        <WrapperContent className={!showCards ? 'mb-4' : ''}>
          {!showCards && <h3 className={titleClass}>Note</h3>}
          <Textarea
            id="note"
            name="note"
            value={formData.note}
            onChange={onChange}
            placeholder="Note interne sul cliente..."
            rows={compact ? 2 : 4}
          />
        </WrapperContent>
      </Wrapper>

      {/* Opzioni */}
      <Wrapper>
        {showCards && (
          <WrapperHeader>
            <WrapperTitle>Opzioni</WrapperTitle>
          </WrapperHeader>
        )}
        <WrapperContent className={contentClass}>
          {!showCards && <h3 className={titleClass}>Opzioni</h3>}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="attivo">Cliente Attivo</Label>
              <p className="text-sm text-muted-foreground">
                Se disattivato, il cliente non sarà visibile nelle ricerche
              </p>
            </div>
            <Switch
              id="attivo"
              checked={formData.attivo}
              onCheckedChange={(checked) => onSwitchChange('attivo', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="disponibile_ecommerce">Disponibile E-commerce</Label>
              <p className="text-sm text-muted-foreground">
                Se attivo, il cliente può essere sincronizzato con Shopify
              </p>
            </div>
            <Switch
              id="disponibile_ecommerce"
              checked={formData.disponibile_ecommerce}
              onCheckedChange={(checked) => onSwitchChange('disponibile_ecommerce', checked)}
            />
          </div>
        </WrapperContent>
      </Wrapper>
    </div>
  )
}
